import React, { FC, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SavedAddress } from '../apollo/generated/TakeoutWebGraphQLOperations'
import { useApolloClient } from '@apollo/client'
import { useUpdateAddressMutation } from './account-mutations'
import { alertError, alertSuccess } from '../auth/alertUtils'
import InputField from '../auth/InputField'
import { AddressAutocomplete } from '../components/AddressAutocomplete/AddressAutocomplete'
import styles from './account.module.css'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@toasttab/buffet-pui-buttons'
import { ButtonVariant } from '@local/do-secundo-button'

interface EditAddressProps {
  address: SavedAddress
  onSuccessfulUpdate: () => void
}

interface EditAddressFormData {
  addressLabel: string
  address: {
    address1: string
    address2?: string
    city: string
    state: string
    zipCode: string
    latitude: number
    longitude: number
  }
  notes?: string
}

export const EditAddress: FC<EditAddressProps> = ({
  address,
  onSuccessfulUpdate
}) => {
  // Define the Yup schema with the same structure
  const validationSchema: Yup.SchemaOf<EditAddressFormData> = Yup.object({
    addressLabel: Yup.string().required('Required'),
    address: Yup.object({
      address1: Yup.string().required('Required'),
      address2: Yup.string().notRequired(), // Optional
      city: Yup.string().required('Required'),
      state: Yup.string().required('Required'),
      zipCode: Yup.string().required('Required'),
      latitude: Yup.number().required('Required'),
      longitude: Yup.number().required('Required')
    }).required(),
    notes: Yup.string().notRequired() // Optional
  })

  const defaultValues: EditAddressFormData = {
    addressLabel: address.name ?? '',
    address: {
      ...address.deliveryInfo,
      address2: address.deliveryInfo.address2 ?? undefined
    },
    notes: address.deliveryInfo.notes ?? ''
  }

  const [UpdateAddressMutation] = useUpdateAddressMutation()
  const apolloClient = useApolloClient()
  const formMethods = useForm<EditAddressFormData>({
    mode: 'onChange',
    defaultValues: defaultValues,
    // @ts-ignore
    resolver: yupResolver(validationSchema)
  })

  const onSubmit = useCallback(async () => {
    try {
      const formValues = formMethods.getValues()
      const { data } = await UpdateAddressMutation({
        variables: {
          input: {
            name: formValues.addressLabel,
            deliveryInfo: {
              // need to specifically rebuild the object here since any unexpected fields
              // (such as __typename which is present on the saved address) result in an error
              address1: formValues.address.address1,
              address2: formValues.address.address2,
              city: formValues.address.city,
              latitude: formValues.address.latitude,
              longitude: formValues.address.longitude,
              notes: formValues.notes,
              state: formValues.address.state,
              zipCode: formValues.address.zipCode
            },
            guid: address.guid
          }
        },
        client: apolloClient
      })
      if (!data || data.updateAddress.__typename === 'UpdateAddressError') {
        alertError('Something went wrong, please try again later')
      } else if (data.updateAddress.__typename === 'UpdateAddressResponse') {
        alertSuccess('Address updated successfully')
        onSuccessfulUpdate()
      }
    } catch (e) {
      alertError('Something went wrong, please try again later')
      reportError(e)
    }
  }, [
    UpdateAddressMutation,
    address,
    onSuccessfulUpdate,
    apolloClient,
    formMethods
  ])

  const formValues = formMethods.getValues()

  return (
    <FormProvider {...formMethods}>
      <div className={styles.editAddressWrapper}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <div className={styles.editAddressInputs}>
            <InputField
              id='addressLabel'
              type='text'
              label='Address Label'
              required
            />
            {/* AddressAutocomplete was designed to work with Formik, but we're using React Hook Form here.
                When using setValue directly, we need to explicitly tell React Hook Form to mark the field
                as dirty and touched, otherwise the form won't recognize the changes and the submit button
                will remain disabled. */}
            <AddressAutocomplete
              form={{
                setFieldValue: (
                  fieldName: keyof EditAddressFormData,
                  value: any
                ) => {
                  const newValue = { ...value }

                  formMethods.setValue(fieldName, newValue, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true
                  })
                }
              }}
              field={{
                name: 'address',
                value: formValues.address
              }}
              disabled={false}
            />
            <InputField id='notes' type='text' label='Delivery instructions' />
          </div>

          <div className={'flex space-x-2 justify-end'}>
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={onSuccessfulUpdate}
              className={'w-fit px-4'}
            >
              Cancel
            </Button>
            <Button
              className={'w-fit px-4'}
              variant={ButtonVariant.PRIMARY}
              onClick={onSubmit}
              disabled={
                !(
                  formMethods.formState.isValid && formMethods.formState.isDirty
                ) || formMethods.formState.isSubmitting
              }
            >
              Update my address
            </Button>
          </div>
        </form>
      </div>
    </FormProvider>
  )
}
